/**
 * UC Davis brand colors.
 *
 * Source (ONE first-party source, fact sheet brand.* at `reported` confidence): the www.ucdavis.edu
 * home page markup, 2026-10-29 — #022851 (20 uses, beside class `category-brand--ucd-blue`) and
 * #FFBF00 (9 uses, beside `category-brand--ucd-gold`):
 *
 *   Aggie Blue: #022851 (RGB 2 40 81)   — primary
 *   Aggie Gold: #FFBF00 (RGB 255 191 0) — accent
 *
 * A second first-party source (the communicationsguide.ucdavis.edu brand guide) is Cloudflare-blocked
 * to our client and the catalog's CSS is robots-disallowed, so the second-source check is an owner
 * browser-pass item (fact sheet open_items). Change these values if that pass disagrees.
 *
 * The 50–900 ladders are HSL interpolations around the brand colors (brand value at `700` and as
 * DEFAULT), using the UC Merced theme's lightness steps: brand-aligned for UI use, not an officially
 * published extended palette. Logo policy as UMD / SJSU / UC Merced: text-only branding, no wordmark.
 */
import type { ThemeColors } from '../types';

export const ucdavisColors: ThemeColors = {
  // Primary — Aggie Blue (#022851 = rgb(2, 40, 81))
  '--color-primary':     '2 40 81',
  // -strong: very dark navy, white text ≈ 14.7:1 — the base brand color (v8.13 token rules).
  '--color-primary-strong': '2 40 81',
  '--color-primary-50':  '240 247 255',
  '--color-primary-100': '215 234 254',
  '--color-primary-200': '175 213 253',
  '--color-primary-300': '116 181 252',
  '--color-primary-400': '46 144 250',
  '--color-primary-500': '5 108 219',
  '--color-primary-600': '4 74 149',
  '--color-primary-700': '2 40 81',
  '--color-primary-800': '2 31 62',
  '--color-primary-900': '1 21 42',

  // Accent — Aggie Gold (#FFBF00 = rgb(255, 191, 0))
  '--color-accent':     '255 191 0',
  '--color-accent-50':  '255 251 240',
  '--color-accent-100': '255 245 214',
  '--color-accent-200': '255 235 173',
  '--color-accent-300': '255 219 112',
  '--color-accent-400': '255 201 41',
  '--color-accent-500': '224 168 0',
  '--color-accent-600': '153 115 0',
  '--color-accent-700': '255 191 0',
  '--color-accent-800': '64 48 0',
  '--color-accent-900': '43 32 0',

  // Brand-mark faces (components/ui/BrandMark.tsx).
  //
  // Aggie Blue is, measured, almost the same color as UC Merced's Bobcat Blue: hue 211° vs 212°,
  // lightness 16.3% vs 16.9%. The two UC marks are therefore near-identical in a header — accepted
  // under the owner's 2026-10-14 ruling ("a school's mark follows its own brand"; the cross-theme
  // distance floor was removed because brand colors cluster). Do NOT lighten the front face to
  // separate them: that is the regression documented in themes/ucmerced/colors.ts. The front face IS
  // the brand value; top / side are darker on the same hue. Light mode: 13.1:1 on warm paper.
  '--color-mark-front': '2 40 81',
  '--color-mark-top':   '1 28 57',
  '--color-mark-side':  '1 17 34',
  // Dark mode is re-derived on the same hue: the front face at L 50% reaches 4.56:1 on warm charcoal
  // (UC Merced's ships 4.49:1); top / side read as shadow.
  '--color-mark-front-dm': '6 123 249',
  '--color-mark-top-dm':   '5 93 189',
  '--color-mark-side-dm':  '3 66 134',
};
